import '../css/app.css';

import { createApp, h, DefineComponent, Plugin, App } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { PageProps } from '@inertiajs/core';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { ZiggyVue } from 'ziggy-js';
import i18next from 'i18next';
import I18NextVue from 'i18next-vue';
import ApplicationMark from '@/Components/ApplicationMark.vue';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import corePagesPaths, { type PagesPath } from '@core/utils/pages-paths';
import { setDefaultOptions } from 'date-fns';
import { it, de, enGB } from 'date-fns/locale';
import coreFormPath, { type FormPaths } from '@core/utils/forms-paths';

const appName: string = import.meta.env.VITE_APP_NAME || 'Kiboko';

type CreateInnertiaAppProps = Parameters<typeof createInertiaApp>[0];

// higher priority first
const pages: PagesPath[] = [
    [
        import.meta.glob<DefineComponent>('/resources/js/Pages/**/*.vue'),
        (name) => `/resources/js/Pages/${name}.vue`,
    ],
    ...corePagesPaths,
];

// lower priority first
const forms: FormPaths[] = [
    ...coreFormPath,
    [
        import.meta.glob<DefineComponent>(
            '/resources/js/Components/Forms/**/*.vue',
        ),
        '/resources/js/Components/Forms/',
    ],
];

export async function prepareApp(
    app: App<Element>,
    props: PageProps,
    plugin: Plugin,
    el: Element,
): Promise<App<Element>> {
    try {
        await i18next.init({
            lng: props.locale,
            interpolation: {
                escapeValue: false,
            },
            fallbackLng: false,
            resources: props.translations,
        });

        setDefaultOptions({
            locale:
                {
                    en: enGB,
                    it: it,
                    de: de,
                }[props.locale] ?? enGB,
        });

        app.config.globalProperties.$logos = {
            mark: ApplicationMark,
            logo: ApplicationLogo,
            auth: AuthenticationCardLogo,
        };

        app.config.globalProperties.$forms = {};
        for (const [glob, pathPrefix] of forms) {
            for (const [path, component] of Object.entries(glob)) {
                const name = path
                    .replace(pathPrefix, '')
                    .replace('.vue', '')
                    .replace(/^\//, '');
                app.config.globalProperties.$forms[name] = component;
            }
        }

        app.use(plugin)
            .use(ZiggyVue, {
                ...props.ziggy,
            })
            .use(PrimeVue, {
                theme: 'none',
            })
            .use(I18NextVue, { i18next })
            .use(ConfirmationService)
            .use(ToastService)
            .mount(el);
    } catch (e) {
        console.error("Errore durante l'inizializzazione dell'app:", e);
        el.innerHTML =
            "Si è verificato un errore durante il caricamento dell'applicazione. Riprova più tardi.";
    }

    return app;
}

export async function resolve(name: string): Promise<DefineComponent> {
    for (const path of pages) {
        const [appPages, getPagePath] = path;
        const appPagePath = getPagePath(name);
        if (appPages[appPagePath]) {
            return await resolvePageComponent<DefineComponent>(
                appPagePath,
                appPages,
            );
        }
    }
    throw new Error(
        `Impossibile risolvere la pagina del componente "${name}".`,
    );
}

export function title(title: string): string {
    if (!title || title.trim().length === 0) {
        return appName;
    }
    return `${title} - ${appName}`;
}

createInertiaApp({
    title: title,
    resolve: resolve,
    setup: (async ({
        el,
        App,
        props,
        plugin,
    }: Parameters<CreateInnertiaAppProps['setup']>[0]) => {
        let app = createApp({ render: () => h(App, props) });
        app = await prepareApp(app, props.initialPage.props, plugin, el);
        return app;
    }) as unknown as CreateInnertiaAppProps['setup'],
    progress: {
        color: '#4B5563',
    },
}).catch((e: unknown) => {
    console.error('Error initializing Inertia: ', e);
});
